import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Pencil, RotateCcw, Save } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";

const MegaMenuModal = ({ isOpen, onClose, menuItem }) => {
    // const [columns, setColumns] = useState(
    //     Array(5)
    //         .fill(null)
    //         .map(() => ({
    //             title: "Main Title",
    //             isEditing: false,
    //             lists: [],
    //         }))
    // );

    const [columns, setColumns] = useState([]);
    const url = process.env.REACT_APP_LINK
    const token = decryptToken("accessToken") || ""
    // Fetch menu items from backend
    useEffect(() => {
        if (isOpen && menuItem?.id) {
            axios.get(`${url}/api/megamenu/${menuItem.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
                .then(res => {
                    // Transform backend data into your columns structure
                    const grouped = {};

                    res.data.forEach(item => {
                        if (!grouped[item.title]) grouped[item.title] = [];
                        grouped[item.title].push({ label: item.name, url: item.url, id: item.id });
                    });

                    const cols = Object.keys(grouped).map(title => ({
                        title,
                        isEditing: false,
                        lists: grouped[title]
                    }));

                    setColumns(cols.length ? cols : Array(5).fill(null).map(() => ({
                        title: "Main Title",
                        isEditing: false,
                        lists: [],
                    })));
                })
                .catch(err => console.error(err));
        }
    }, [isOpen, menuItem]);


    const saveMegaMenu = async () => {
        try {
            for (const col of columns) {
                for (const list of col.lists) {
                    if (list.id) {
                        // Update existing
                        await axios.put(`${url}/api/megamenu/${list.id}`, {
                            title: col.title,
                            name: list.label,
                            url: list.url,
                        }, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        });
                    } else {
                        // Create new
                        await axios.post(`${url}/api/megamenu`, {
                            title: col.title,
                            name: list.label,
                            url: list.url,
                            menu_id: menuItem.id,
                            main_id: menuItem.id,
                            main_name: menuItem.label,
                        }, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        });
                    }
                }
            }
            toast.success("Mega Menu saved successfully!");
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Failed to save Mega Menu");
        }
    };


    const updateTitle = (colIdx, value) => {
        const updated = [...columns];
        updated[colIdx].title = value;
        setColumns(updated);
    };

    const toggleEditTitle = (colIdx, editing) => {
        const updated = [...columns];
        updated[colIdx].isEditing = editing;
        setColumns(updated);
    };

    const addListItem = (colIdx) => {
        const updated = [...columns];
        updated[colIdx].lists.push({ label: "", url: "" });
        setColumns(updated);
    };

    const updateListItem = (colIdx, listIdx, field, value) => {
        const updated = [...columns];
        updated[colIdx].lists[listIdx][field] = value;
        setColumns(updated);
    };

    const removeListItem = async (colIdx, listIdx) => {
        const item = columns[colIdx].lists[listIdx];

        if (item.id) {
            try {
                await axios.delete(`${url}/api/megamenu/${item.id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
            } catch (error) {
                console.error("Failed to delete item:", error);
                alert("Error deleting item from backend.");
            }
        }

        const updated = [...columns];
        updated[colIdx].lists.splice(listIdx, 1);
        setColumns(updated);
    };


    const addColumn = () => {
        setColumns([
            ...columns,
            { title: "Main Title", isEditing: false, lists: [] },
        ]);
    };

    const resetAll = async () => {
        if (menuItem?.id) {
            try {
                const res = await axios.get(`${url}/api/megamenu/${menuItem.id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const grouped = {};
                res.data.forEach(item => {
                    if (!grouped[item.title]) grouped[item.title] = [];
                    grouped[item.title].push({ label: item.name, url: item.url, id: item.id });
                });

                const cols = Object.keys(grouped).map(title => ({
                    title,
                    isEditing: false,
                    lists: grouped[title],
                }));

                setColumns(cols);   // restore to original server state
            } catch (err) {
                console.error(err);
                alert("Failed to reset from backend");
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl h-[80vh] p-3 flex flex-col"
            >
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-1 mb-2">
                    <h2 className="text-sm font-semibold text-gray-800">
                        Mega Menu: <span className="text-blue-600">{menuItem?.label}</span>
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Controls */}
                <div className="flex justify-between items-center mb-2">
                    <div className="flex gap-1">
                        <button
                            onClick={addColumn}
                            className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                        >
                            <Plus size={12} /> Column
                        </button>
                        <button
                            onClick={resetAll}
                            className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-200"
                        >
                            <RotateCcw size={12} /> Reset
                        </button>
                    </div>
                    <button
                        onClick={saveMegaMenu}
                        className="flex items-center gap-1 bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                    >
                        <Save size={12} /> Save
                    </button>
                </div>

                {/* Columns Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 overflow-y-auto">
                    {columns.map((col, colIdx) => (
                        <div
                            key={colIdx}
                            className="border rounded p-2 bg-gray-50 text-xs"
                        >
                            {/* Editable Title */}
                            <div className="flex justify-between items-center mb-1">
                                {col.isEditing ? (
                                    <input
                                        type="text"
                                        value={col.title}
                                        onChange={(e) => updateTitle(colIdx, e.target.value)}
                                        onBlur={() => toggleEditTitle(colIdx, false)}
                                        className="border rounded px-1 py-0.5 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        autoFocus
                                    />
                                ) : (
                                    <h3
                                        className="font-semibold text-gray-800 cursor-pointer hover:text-blue-600 text-xs"
                                        onClick={() => toggleEditTitle(colIdx, true)}
                                    >
                                        {col.title}
                                    </h3>
                                )}
                                {!col.isEditing && (
                                    <button
                                        onClick={() => toggleEditTitle(colIdx, true)}
                                        className="text-gray-400 hover:text-gray-700"
                                    >
                                        <Pencil size={12} />
                                    </button>
                                )}
                            </div>

                            {/* List Items */}
                            <div className="space-y-1">
                                {col.lists.map((list, listIdx) => (
                                    <div
                                        key={listIdx}
                                        className="bg-white p-1 rounded border text-xs"
                                    >
                                        <input
                                            type="text"
                                            placeholder="Name"
                                            value={list.label}
                                            onChange={(e) =>
                                                updateListItem(colIdx, listIdx, "label", e.target.value)
                                            }
                                            className="w-full border rounded px-1 py-0.5 text-xs mb-1 focus:outline-none focus:ring-1 focus:ring-blue-300"
                                        />
                                        <input
                                            type="text"
                                            placeholder="URL"
                                            value={list.url}
                                            onChange={(e) =>
                                                updateListItem(colIdx, listIdx, "url", e.target.value)
                                            }
                                            className="w-full border rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-300"
                                        />
                                        <button
                                            onClick={() => removeListItem(colIdx, listIdx)}
                                            className="mt-1 text-[10px] text-red-600 flex items-center gap-0.5 hover:underline hover:text-red-800"
                                        >
                                            <Trash2 size={10} /> Remove
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add List (link style instead of button) */}
                            <button
                                onClick={() => addListItem(colIdx)}
                                className="mt-2 w-full flex items-center justify-center gap-1 text-[11px] text-blue-600 hover:underline"
                            >
                                <Plus size={12} /> Add Item
                            </button>
                        </div>
                    ))}
                </div>

            </motion.div>
        </div>
    );
};

export default MegaMenuModal;
